import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Popover } from 'primeng/popover';
import { MobileWidthCheckerService } from '@services/mobile-width-checker.service';
import { AuthService, IOwnerStore } from '@services/auth.service';

@Component({
  selector: 'app-topbar',
  standalone: false,

  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.scss'
})
export class TopbarComponent implements OnInit {
  @Output() toggleSidebar = new EventEmitter<void>();
  isMobileScreen$ = this.mobileWidthCheckerService.isMobileScreen$;

  showStorePicker = false;
  ownerStores: IOwnerStore[] = [];
  storesLoading = false;
  storeSwitchInFlight = false;
  hasMultipleStores = false;
  currentStoreId: string | null = null;
  currentStoreName: { ar: string; en: string } | null = null;
  currentLang: string = 'ar';

  constructor(
    private mobileWidthCheckerService: MobileWidthCheckerService,
    private router: Router,
    private authService: AuthService,
    private translate: TranslateService,
    private messageService: MessageService
  ) {
    this.currentStoreName = this.authService.getSelectedStoreName();
    this.currentLang = this.translate.currentLang || this.translate.getDefaultLang() || 'ar';
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
    });
  }

  ngOnInit(): void {
    this.loadOwnerStores();
  }

  get displayStoreName(): string {
    if (!this.currentStoreName) return '';
    return this.currentLang === 'en'
      ? this.currentStoreName.en || this.currentStoreName.ar
      : this.currentStoreName.ar || this.currentStoreName.en;
  }

  /**
   * Loads the owner's stores on init to decide whether the header store badge
   * should behave as a switcher (multiple stores) or a plain label (single store).
   * Fails silently so a failed load never surfaces a toast on page load.
   */
  private loadOwnerStores() {
    this.currentStoreId = this.authService.getSelectedStoreId();
    this.authService.getOwnerStores().subscribe({
      next: (res: any) => {
        if (res?.statusCode === 200) {
          this.ownerStores = res.data ?? [];
          this.hasMultipleStores = this.ownerStores.length > 1;
          this.ensureCurrentStoreName();
        }
      },
      error: () => {
        // Silent: badge falls back to a non-switchable label.
      },
    });
  }

  /** Fills the store name from the loaded list when it isn't already persisted. */
  private ensureCurrentStoreName() {
    if (this.currentStoreName) return;
    // Single-store owners have no selection step, so just use their only store.
    if (this.ownerStores.length === 1) {
      this.currentStoreName = this.ownerStores[0].name;
      this.currentStoreId = this.ownerStores[0]._id;
      return;
    }
    if (!this.currentStoreId) return;
    const match = this.ownerStores.find((store) => store._id === this.currentStoreId);
    if (match) {
      this.currentStoreName = match.name;
    }
  }

  /** Opens the store switcher dialog from the header badge (multiple stores only). */
  openStorePicker() {
    if (!this.hasMultipleStores) return;
    this.currentStoreId = this.authService.getSelectedStoreId();
    this.showStorePicker = true;
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  navigateToSettings(op?: Popover) {
    op?.hide();
    this.router.navigate(['/settings']);
  }

  onStoreSwitched(storeId: string) {
    if (this.storeSwitchInFlight) return;
    this.storeSwitchInFlight = true;
    this.storesLoading = true;
    this.authService.selectStore(storeId).subscribe({
      next: (res: any) => {
        if (res.statusCode === 200) {
          this.authService.persistAuthTokens(res.data);
          this.authService.persistSelectedStore(res.data.storeId, res.data.storeName);
          window.location.href = '/';
        } else {
          this.storesLoading = false;
          this.storeSwitchInFlight = false;
          this.showErrorToast(res?.message);
        }
      },
      error: (err) => {
        this.storesLoading = false;
        this.storeSwitchInFlight = false;
        this.showErrorToast(err?.error?.message);
      },
    });
  }

  private showErrorToast(message: any) {
    const isLanguageEnglish =
      (this.translate.currentLang || this.translate.getDefaultLang() || 'ar') === 'en';
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('error'),
      detail: isLanguageEnglish ? message?.en : message?.ar,
    });
  }
}
